/**
 * Find the real element a walkthrough step points at.
 *
 * Resolution order mirrors how the UI map records controls: a stable
 * `data-ai-id` first, then the accessible name, then visible text. Labels work
 * from day one with no component changes, which is why they are supported at
 * all; `data-ai-id` is added over time to controls where label matching proves
 * fragile (duplicate labels across modals are the likely failure).
 *
 * A miss is never silent — the caller degrades the step to text rather than
 * pointing at nothing.
 */

export type TargetBy = "testid" | "label" | "aria";

export interface WalkthroughTarget {
  by?: TargetBy;
  value: string;
  /**
   * The control lives in a row's right-click menu, so it is in no DOM until
   * that menu is open. Set by the backend from the interface map; the overlay
   * uses it to say what actually has to be opened instead of guessing that the
   * therapist is on the wrong screen.
   */
  in_right_click_menu?: boolean;
}

const CLICKABLE =
  'button, a, input, select, textarea, [role="button"], [role="tab"], [onclick]';

const visible = (el: Element): boolean => {
  const rect = el.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return false;
  const style = window.getComputedStyle(el);
  return style.visibility !== "hidden" && style.display !== "none" && style.opacity !== "0";
};

const normalise = (text: string | null | undefined): string =>
  (text || "").replace(/\s+/g, " ").trim().toLowerCase();

const zIndexOf = (el: Element): number =>
  parseInt(window.getComputedStyle(el).zIndex, 10) || 0;

/**
 * Anything covering the screen above the page: a modal, a popup, a drawer.
 *
 * `[role="dialog"]` covers everything built on ModalWrapper. The computed-style
 * sweep is the safety net for the modals that render their own container — this
 * app has ~54 of them and not all go through the wrapper.
 */
const overlayRoots = (): HTMLElement[] => {
  // The guide's own overlay is not a popup to search inside. Its scrim carries
  // no controls so the sweep below already misses it, but naming the exclusion
  // means a future callout with a button in it cannot make the guide start
  // highlighting its own Next button.
  const mine = (el: Element) => el.closest("[data-ai-walkthrough]") !== null;
  const tagged = Array.from(
    document.querySelectorAll<HTMLElement>('[role="dialog"], .modal, [data-ai-modal]')
  ).filter((el) => !mine(el));
  const sniffed = Array.from(document.body.querySelectorAll<HTMLElement>("div")).filter((el) => {
    const style = window.getComputedStyle(el);
    return (
      style.position === "fixed" &&
      zIndexOf(el) >= 900 &&
      el.getBoundingClientRect().width > window.innerWidth * 0.5 &&
      el.querySelector(CLICKABLE) !== null
    );
  });
  return Array.from(new Set([...tagged, ...sniffed]))
    .filter((el) => !mine(el))
    .filter(visible);
};

/**
 * Search the topmost overlay first. A modal sits over the screen beneath, and
 * the screen underneath very often carries a control with a similar label.
 */
const searchRoots = (): Element[] => {
  const overlays = overlayRoots();
  const roots: Element[] = [];
  if (overlays.length) {
    roots.push(overlays.reduce((best, el) => (zIndexOf(el) >= zIndexOf(best) ? el : best)));
  }
  roots.push(document.body);
  return roots;
};

/**
 * The bounding box of the topmost open modal, if one is showing.
 *
 * A walkthrough callout has to sit clear of it: drawn over the modal it hides
 * the very controls the step is asking the therapist to use.
 */
export const topOverlayRect = (): DOMRect | null => {
  const overlays = overlayRoots();
  if (!overlays.length) return null;
  const topmost = overlays.reduce((best, el) => (zIndexOf(el) >= zIndexOf(best) ? el : best));
  const rect = topmost.getBoundingClientRect();
  // A full-viewport scrim is not something to dodge -- only the panel inside it.
  if (rect.width >= window.innerWidth * 0.95 && rect.height >= window.innerHeight * 0.95) {
    const inner = Array.from(topmost.children).find(
      (c) => c.getBoundingClientRect().width < window.innerWidth * 0.9
    );
    return inner ? inner.getBoundingClientRect() : null;
  }
  return rect;
};

/**
 * The row to right-click, for a step whose control is a right-click option.
 *
 * Such an option is in no DOM until the menu is open, so there is nothing to
 * highlight and the guide reads as broken. Pointing at a row instead puts the
 * halo where the therapist has to act; the instruction already tells them to
 * right-click it.
 *
 * It goes through `searchRoots` for the same reason everything else does: with
 * the meetings window open over a patient page, a plain document-wide query
 * found a row of the table BEHIND the modal and drew the halo out in the
 * margin, over a table the step has nothing to do with. The topmost overlay is
 * searched first, and only its rows can win.
 *
 * The first body row, deliberately, and not a guess at which record was meant:
 * the menu is the same on every row, so the highlight is showing the gesture,
 * not choosing the record.
 */
export const rowToRightClick = (target?: WalkthroughTarget): HTMLElement | null => {
  if (!target?.in_right_click_menu || typeof document === "undefined") return null;
  for (const root of searchRoots()) {
    const rows = Array.from(root.querySelectorAll<HTMLElement>("table tbody tr"))
      .filter(visible)
      // A spacer or an empty-state row has no cells and nothing to act on.
      .filter((row) => row.querySelector("td") !== null);
    if (rows.length) return rows[0];
  }
  return null;
};

export const resolveTarget = (target: WalkthroughTarget): HTMLElement | null => {
  const wanted = normalise(target.value);
  if (!wanted) return null;

  for (const root of searchRoots()) {
    // 1. the stable id, when the control has one
    const byId = root.querySelector<HTMLElement>(`[data-ai-id="${CSS.escape(target.value)}"]`);
    if (byId && visible(byId)) return byId;

    const candidates = Array.from(root.querySelectorAll<HTMLElement>(CLICKABLE)).filter(visible);

    // Score rather than take the first hit, so a weak match cannot beat a
    // strong one that appears later in the DOM.
    let best: { el: HTMLElement; score: number } | null = null;
    for (const el of candidates) {
      const aria = normalise(el.getAttribute("aria-label"));
      const title = normalise(el.getAttribute("title"));
      const placeholder = normalise((el as HTMLInputElement).placeholder);
      const text = normalise(el.textContent);

      let score = 0;
      if (aria === wanted || title === wanted || placeholder === wanted) score = 100;
      else if (text === wanted) score = 90;
      else if (text && text.includes(wanted)) score = 70;
      else if (aria && aria.includes(wanted)) score = 60;
      else if (title && title.includes(wanted)) score = 55;
      // The candidate's own text being contained in what we asked for is the
      // weakest signal and needs a guard: without it a button reading "S" or
      // "×" matches almost anything, which is how the toolbar icon once won
      // over "Open Request".
      else if (
        text &&
        text.length >= 4 &&
        text.length >= wanted.length * 0.6 &&
        wanted.includes(text)
      ) {
        score = 40;
      }

      if (score && (!best || score > best.score)) best = { el, score };
    }
    if (best) return best.el;
  }

  return null;
};

/**
 * The id of the popup currently on screen, from its own `data-ai-modal`.
 *
 * Declared by the page rather than inferred: the modals tag themselves with the
 * same component id the interface map uses, so "is the meetings window open"
 * is a lookup instead of a guess about z-indexes and element sizes. Returns the
 * topmost when popups are stacked, and null when none carries an id.
 */
export const openModalId = (): string | null => {
  const tagged = Array.from(
    document.querySelectorAll<HTMLElement>("[data-ai-modal]")
  ).filter((el) => el.closest("[data-ai-walkthrough]") === null).filter(visible);
  if (!tagged.length) return null;
  const topmost = tagged.reduce((best, el) =>
    (zIndexOf(el) >= zIndexOf(best) ? el : best));
  return topmost.getAttribute("data-ai-modal") || null;
};
